import { useEffect, useState } from "react";
import { Menu, X, Crown } from "lucide-react";
import { NAV_LINKS, COMPANY } from "@/lib/siteContent";

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (e, href) => {
    if (href.startsWith("#")) {
      e.preventDefault();
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      setOpen(false);
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-all duration-300 ${
        scrolled ? "border-b border-[#E5E7EB] shadow-[0_1px_0_rgba(15,42,68,0.04)]" : "border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a
          href="#top"
          onClick={(e) => handleAnchor(e, "#top")}
          className="flex items-center gap-2 group"
          data-testid="nav-logo"
        >
          <Crown
            className="h-5 w-5 text-[#D4AF7F] group-hover:text-[#0F2A44] transition-colors"
            strokeWidth={1.5}
          />
          <span className="font-serif text-xl text-[#0F2A44] tracking-tight">
            {COMPANY.shortName}
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((link) => (
            <a
              key={link.id}
              href={link.href}
              onClick={(e) => handleAnchor(e, link.href)}
              data-testid={`nav-link-${link.id}`}
              className="text-sm tracking-wide text-[#1A1A1A] hover:text-[#D4AF7F] transition-colors"
            >
              {link.label}
            </a>
          ))}
          <a
            href={COMPANY.phoneHref}
            data-testid="nav-phone"
            className="text-sm tracking-wide text-[#0F2A44] border-b-2 border-[#D4AF7F] pb-0.5 hover:text-[#D4AF7F] transition-colors"
          >
            {COMPANY.phone}
          </a>
        </nav>

        <button
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((s) => !s)}
          data-testid="nav-mobile-toggle"
          className="md:hidden p-2 -mr-2 text-[#0F2A44]"
        >
          {open ? <X className="h-6 w-6" strokeWidth={1.5} /> : <Menu className="h-6 w-6" strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#E5E7EB] bg-white" data-testid="nav-mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {NAV_LINKS.map((link) => (
              <a
                key={link.id}
                href={link.href}
                onClick={(e) => handleAnchor(e, link.href)}
                data-testid={`nav-mobile-link-${link.id}`}
                className="text-base text-[#1A1A1A] hover:text-[#D4AF7F] transition-colors"
              >
                {link.label}
              </a>
            ))}
            <a
              href={COMPANY.phoneHref}
              data-testid="nav-mobile-phone"
              className="text-base text-[#0F2A44] border-b-2 border-[#D4AF7F] pb-1 w-fit hover:text-[#D4AF7F] transition-colors"
            >
              {COMPANY.phone}
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
